import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "@phosphor-icons/react";
import { formatINR } from "@/lib/format";
import { REEDOS_PRINT_STYLES } from "@/lib/reedosPrintStyles";
import { api } from "@/lib/api";
import { getPrototype, getCurrentPrototypeId, migratePrototypeShape, DELIVERY_MODES } from "./builderStorage";
import { aggregateScopeByProduct } from "./productAggregation";
import { DeliveryIcon } from "./deliveryIcons";

/**
 * Proposal Builder Prototype — COMMERCIALS-ONLY preview.
 *
 * Iter 97.15 — Unified 3-mode rendering (operator directive 2026-02-27).
 * The same DOM structure is rendered across Itemized / Grouped /
 * Consolidated; only price visibility + subtotal visibility toggle:
 *
 *   - Itemized     → all prices visible, subtotals visible, grand total visible
 *   - Grouped      → module-row prices BLANK (""), subtotals visible, grand total visible
 *   - Consolidated → all row prices BLANK (""), subtotals HIDDEN, only grand total
 *
 * Option bands:
 *   - Multi-module products → "OPTION · <PRODUCT NAME>" (own band)
 *   - Atomic standalones    → grouped under "OPTION · OTHER ITEMS"
 *   - Operator Builder group → band label wins for products in the group
 *
 * Blank cells are empty strings — no dash, no zero, no &nbsp; (operator
 * confirmation 2026-02-27, clarification message #1).
 *
 * Grand Total in Consolidated mode = computed sum of underlying prices
 * (no manual override UI — operator approved 2026-02-27).
 */
export default function PreviewPage() {
    const navigate = useNavigate();
    const { id: proposalId } = useParams();
    const prototypeId = proposalId ? `proposal-${proposalId}` : null;
    const [searchParams] = useSearchParams();
    const versionId = searchParams.get("v");
    const [prototype, setPrototype] = useState(null);
    const [loading, setLoading] = useState(true);

    /* The Builder stores one prototype per version (`proposal-{id}-v-{versionId}`).
       Resolve the requested version only (never another version's draft); without
       `v`, use the Builder's current one, else the legacy proposal-scoped draft.
       No local draft (e.g. a fresh browser) → the version's saved builder snapshot. */
    useEffect(() => {
        if (!prototypeId) { setPrototype(null); setLoading(false); return; }
        let cancelled = false;
        const current = getCurrentPrototypeId();
        const local = versionId
            ? getPrototype(`${prototypeId}-v-${versionId}`)
            : (current?.startsWith(`${prototypeId}-v-`) && getPrototype(current)) || getPrototype(prototypeId);
        if (local) { setPrototype(local); setLoading(false); return; }
        setLoading(true);
        api.get(`/billing/proposal/${proposalId}`)
            .then(({ data }) => {
                const active = (data.versions || []).filter((x) => (x.status || "active") !== "cancelled");
                const v = versionId
                    ? (data.versions || []).find((x) => x.id === versionId)
                    : active.reduce((a, b) => (!a || b.version_number > a.version_number ? b : a), null);
                const bs = v?.builder_state;
                if (!cancelled) setPrototype(bs ? migratePrototypeShape({ ...bs, currency: data.currency || bs.currency || "INR" }) : null);
            })
            .catch(() => { if (!cancelled) setPrototype(null); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [prototypeId, proposalId, versionId]);

    const products = useMemo(() => aggregateScopeByProduct(prototype?.scope || []), [prototype?.scope]);

    if (loading) {
        return <div className="p-8 text-slate-500" data-testid="preview-loading">Loading preview…</div>;
    }

    if (!prototype) {
        return (
            <div className="p-8 text-slate-500" data-testid="preview-empty">
                No commercials drafted yet for this proposal.{" "}
                <Button variant="link" onClick={() => navigate(`/proposals/${proposalId}/builder`)}>Open Proposal Builder</Button>
            </div>
        );
    }

    const m = prototype.commercials || {};
    const mode = m.presentationMode || "itemized";
    const showModulePrices = mode === "itemized";
    const showAtomicPrices = mode !== "consolidated";
    const showSubtotals = mode !== "consolidated";
    const currency = prototype.currency || "INR";
    const OTHER_ITEMS_LABEL = "OTHER ITEMS";

    const amcByScopeRowId = {};
    (prototype.amc?.items || []).forEach((a) => { amcByScopeRowId[a.scopeRowId] = a; });

    const productSetupSubtotal = (p) =>
        p.scopeRows.reduce((s, r) => s + (Number(m.itemPrices?.[r.id]) || 0), 0);
    const productAmcSubtotal = (p) =>
        p.scopeRows.reduce((s, r) => s + (r.recurring_kind === "annual_cost" ? 0 : (Number(amcByScopeRowId[r.id]?.amount) || 0)), 0);
    /* Annual Cost (2026-10) — own column, shown whenever any item uses it
       (even when the selected item is Complimentary). */
    const isAnnualRow = (r) => r.recurring_kind === "annual_cost";
    const anyAnnual = (prototype.scope || []).some(isAnnualRow);
    const rowAnnual = (r) => (isAnnualRow(r) && r.annual_cost_pricing_mode !== "complimentary"
        ? (Number(m.annualCostPrices?.[r.id]) || 0) : 0);
    const productAnnualSubtotal = (p) => p.scopeRows.reduce((s, r) => s + rowAnnual(r), 0);
    const annualCell = (rows, visible, testid) => {
        if (!anyAnnual) return null;
        const comp = rows.some((r) => isAnnualRow(r) && r.annual_cost_pricing_mode === "complimentary");
        const val = rows.reduce((s, r) => s + rowAnnual(r), 0);
        return (
            <td className="figure" data-testid={testid}>
                {comp ? <span className="complimentary-cell">Complimentary</span> : (visible && val > 0 ? formatINR(val, currency) : "")}
            </td>
        );
    };
    /* AMC column only when AMC is explicitly included (priced or Complimentary). */
    const showAmcCol = (prototype.scope || []).some((r) => !isAnnualRow(r)
        && (r.amc_pricing_mode === "complimentary" || Number(amcByScopeRowId[r.id]?.amount) > 0));
    const cols = 3 + (showAmcCol ? 1 : 0) + (anyAnnual ? 1 : 0);

    /* Build an ordered list of bands. Each band = one "OPTION · <label>"
       section. Operator Builder groups become their own bands first; then
       multi-module products that aren't in any group get their own
       per-product band; finally all atomic standalones that aren't in any
       group collapse into a single "OTHER ITEMS" band. */
    const groupByProductKey = {};
    (m.groups || []).forEach((g) => {
        (g.productKeys || []).forEach((k) => {
            if (!groupByProductKey[k]) groupByProductKey[k] = g;
        });
    });

    const bands = [];

    // 1. Operator-defined Builder groups — preserve order.
    (m.groups || []).forEach((g) => {
        const assignedProducts = (g.productKeys || [])
            .map((k) => products.find((p) => p.key === k))
            .filter(Boolean);
        if (assignedProducts.length === 0) return;
        bands.push({
            id: `grp-${g.id}`,
            label: g.label || "Option",
            products: assignedProducts,
        });
    });

    // 2. Unassigned multi-module products — one band each.
    products.forEach((p) => {
        if (groupByProductKey[p.key]) return;
        if (!p.hasModules) return;
        bands.push({
            id: `prod-${p.key}`,
            label: p.label,
            products: [p],
        });
    });

    // 3. Unassigned atomic standalones — all under ONE "OTHER ITEMS" band.
    const atomicStandalones = products.filter(
        (p) => !groupByProductKey[p.key] && !p.hasModules,
    );
    if (atomicStandalones.length > 0) {
        bands.push({
            id: "band-other-items",
            label: OTHER_ITEMS_LABEL,
            products: atomicStandalones,
        });
    }

    const grandSetup = products.reduce((s, p) => s + productSetupSubtotal(p), 0);
    const grandAmc = products.reduce((s, p) => s + productAmcSubtotal(p), 0);
    const grandAnnual = products.reduce((s, p) => s + productAnnualSubtotal(p), 0);

    const discount = m.discount || { type: "percent", value: 0, reason: "" };
    const discVal = Number(discount.value) || 0;
    const discountAmount =
        discVal <= 0
            ? 0
            : discount.type === "percent"
                ? Math.min(grandSetup, (grandSetup * discVal) / 100)
                : Math.min(grandSetup, discVal);
    const finalSetupFee = Math.max(0, grandSetup - discountAmount);

    const gstShown = prototype.taxDisplay?.mode === "show_gst";
    const gstPct = Number(prototype.taxDisplay?.gstPercent) || 18;
    const setupGst = gstShown ? finalSetupFee * (gstPct / 100) : 0;
    const grandTotalPayable = finalSetupFee + setupGst;

    const modeByCode = Object.fromEntries(DELIVERY_MODES.map((d) => [d.code, d]));

    /* Build the set of delivery modes actually selected across any product,
       so the legend at the bottom enumerates only vocabulary used. */
    const usedDeliveryCodes = (() => {
        const used = new Set();
        const deliveryMap = prototype.commercials?.deliveryModes || {};
        products.forEach((p) => {
            if (p.noDeliveryModes) return;
            (deliveryMap[p.key] || []).forEach((c) => {
                if (modeByCode[c]) used.add(c);
            });
        });
        return DELIVERY_MODES.filter((m) => used.has(m.code));
    })();

    /* Render helpers — unified across modes. */
    const productHeaderRow = (product, counter) => {
        const deliveryCodes = product.noDeliveryModes
            ? []
            : (prototype.commercials?.deliveryModes?.[product.key] || []);
        return (
            <tr key={`hdr-${product.key}`} className="itemized-product-header" data-testid={`preview-product-${product.key}`}>
                <td className="num-col">{String(counter).padStart(2, "0")}</td>
                <td>
                    <div className="product-name">{product.label}</div>
                    {product.description && (
                        <div className="product-sub whitespace-pre-line">{product.description}</div>
                    )}
                    {deliveryCodes.length > 0 && (
                        <div className="capability-icons" data-testid={`preview-product-delivery-${product.key}`}>
                            {deliveryCodes.map((c) => {
                                const dm = modeByCode[c];
                                if (!dm) return null;
                                return <DeliveryIcon key={c} iconName={dm.iconName} label={dm.label} size={13} />;
                            })}
                        </div>
                    )}
                </td>
                <td className="figure"></td>
                {showAmcCol && <td className="figure"></td>}
                {anyAnnual && <td className="figure"></td>}
            </tr>
        );
    };

    const moduleRow = (r) => {
        const setup = Number(m.itemPrices?.[r.id]) || 0;
        const amc = Number(amcByScopeRowId[r.id]?.amount) || 0;
        // Complimentary marker (2026-07-23) — display "Complimentary" in
        // place of the ₹0/blank cell so operators and clients can
        // clearly see that a component is intentionally free. Absent
        // marker on legacy rows → treat as "priced" (unchanged render).
        const setupComp = r.setup_fee_pricing_mode === "complimentary";
        const amcComp = !isAnnualRow(r) && r.amc_pricing_mode === "complimentary";
        const setupCell = setupComp
            ? <span className="complimentary-cell">Complimentary</span>
            : (showModulePrices && setup > 0 ? formatINR(setup, currency) : "");
        const amcCell = amcComp
            ? <span className="complimentary-cell">Complimentary</span>
            : (showModulePrices && amc > 0 ? formatINR(amc, currency) : "");
        return (
            <tr key={`mod-${r.id}`} className="itemized-module-row" data-testid={`preview-item-${r.id}`}>
                <td className="num-col"></td>
                <td>
                    <div className="scope-module-line">↳ {r.moduleLabel || r.productName}</div>
                </td>
                <td className="figure" data-testid={`preview-item-setup-${r.id}`}>
                    {setupCell}
                </td>
                {showAmcCol && (
                    <td className="figure" data-testid={`preview-item-amc-${r.id}`}>
                        {isAnnualRow(r) ? "" : amcCell}
                    </td>
                )}
                {annualCell([r], showModulePrices, `preview-item-annual-cost-${r.id}`)}
            </tr>
        );
    };

    const atomicRow = (product, counter) => {
        const setupSub = productSetupSubtotal(product);
        const amcSub = productAmcSubtotal(product);
        // Complimentary marker (2026-07-23) — atomic products have a
        // single scope row. If that row's Setup/AMC is complimentary,
        // render "Complimentary" instead of the ₹0 aggregate.
        const atomicScopeRow = product.scopeRows?.[0];
        const setupComp = atomicScopeRow?.setup_fee_pricing_mode === "complimentary";
        const amcComp = atomicScopeRow?.amc_pricing_mode === "complimentary" && !isAnnualRow(atomicScopeRow);
        const deliveryCodes = product.noDeliveryModes
            ? []
            : (prototype.commercials?.deliveryModes?.[product.key] || []);
        const setupCell = setupComp
            ? <span className="complimentary-cell">Complimentary</span>
            : (showAtomicPrices && setupSub > 0 ? formatINR(setupSub, currency) : "");
        const amcCell = amcComp
            ? <span className="complimentary-cell">Complimentary</span>
            : (showAtomicPrices && amcSub > 0 ? formatINR(amcSub, currency) : "");
        return (
            <tr key={`atomic-${product.key}`} data-testid={`preview-product-${product.key}`}>
                <td className="num-col">{String(counter).padStart(2, "0")}</td>
                <td>
                    <div className="product-name">{product.label}</div>
                    {product.description && (
                        <div className="product-sub whitespace-pre-line">{product.description}</div>
                    )}
                    {deliveryCodes.length > 0 && (
                        <div className="capability-icons" data-testid={`preview-product-delivery-${product.key}`}>
                            {deliveryCodes.map((c) => {
                                const dm = modeByCode[c];
                                if (!dm) return null;
                                return <DeliveryIcon key={c} iconName={dm.iconName} label={dm.label} size={13} />;
                            })}
                        </div>
                    )}
                </td>
                <td className="figure" data-testid={`preview-product-setup-${product.key}`}>
                    {setupCell}
                </td>
                {showAmcCol && (
                    <td className="figure" data-testid={`preview-product-amc-${product.key}`}>
                        {amcCell}
                    </td>
                )}
                {annualCell(product.scopeRows || [], showAtomicPrices, `preview-product-annual-cost-${product.key}`)}
            </tr>
        );
    };

    /* Compose the table body band by band. */
    const rows = [];
    bands.forEach((band) => {
        rows.push(
            <tr key={`band-${band.id}`} className="option-band">
                <td colSpan={cols} data-testid={`preview-band-${band.id}`}>
                    Product · {band.label}
                </td>
            </tr>,
        );
        let counter = 0;
        band.products.forEach((p) => {
            counter += 1;
            if (p.hasModules) {
                rows.push(productHeaderRow(p, counter));
                p.scopeRows.forEach((r) => rows.push(moduleRow(r)));
            } else {
                rows.push(atomicRow(p, counter));
            }
        });
        if (showSubtotals) {
            const bSetup = band.products.reduce((s, p) => s + productSetupSubtotal(p), 0);
            const bAmc = band.products.reduce((s, p) => s + productAmcSubtotal(p), 0);
            const bAnnual = band.products.reduce((s, p) => s + productAnnualSubtotal(p), 0);
            rows.push(
                <tr key={`sub-${band.id}`} className="option-subtotal" data-testid={`preview-band-subtotal-${band.id}`}>
                    <td colSpan={2} style={{ textAlign: "right" }}>Subtotal — {band.label}</td>
                    <td className="figure figure-em" data-testid={`preview-band-setup-${band.id}`}>
                        {bSetup > 0 ? formatINR(bSetup, currency) : ""}
                    </td>
                    {showAmcCol && (
                        <td className="figure figure-em" data-testid={`preview-band-amc-${band.id}`}>
                            {bAmc > 0 ? formatINR(bAmc, currency) : ""}
                        </td>
                    )}
                    {anyAnnual && (
                        <td className="figure figure-em" data-testid={`preview-band-annual-cost-${band.id}`}>
                            {bAnnual > 0 ? formatINR(bAnnual, currency) : ""}
                        </td>
                    )}
                </tr>,
            );
        }
    });

    return (
        <div className="min-h-screen bg-slate-100 print:bg-white">
            <div className="bg-white border-b border-slate-200 p-3 flex items-center gap-3 sticky top-0 z-10 print:hidden" data-testid="preview-toolbar">
                <Button variant="outline" size="sm" onClick={() => navigate(`/proposals/${proposalId}/builder`)} data-testid="preview-back-btn">
                    <ArrowLeft className="mr-1" /> Back to builder
                </Button>
                <Button size="sm" onClick={() => window.print()} data-testid="preview-print-btn">
                    <Printer className="mr-1" /> Print / Save as PDF
                </Button>
                <div className="text-xs text-slate-500 ml-auto">
                    Commercials-only preview · drops into Section 02 of the production REEDOS proposal template
                </div>
            </div>

            <div className="print-shell" data-testid="preview-document">
                <section className="section" data-testid="preview-commercials">
                    <div className="section-title">
                        <div className="eyebrow"><span className="num">02</span>Commercials</div>
                        <h2>Software &amp; Annual Maintenance</h2>
                        <div className="accent-rule"></div>
                    </div>

                    <div className="commercials-card">
                        <table className="commercials" data-testid="preview-commercial-table">
                            <thead>
                                <tr>
                                    <th className="text-left num-col">Sr.</th>
                                    <th className="text-left">Product</th>
                                    <th className="text-right" style={{ width: "36mm" }}>Software &amp; Set-up Fee</th>
                                    {showAmcCol && <th className="text-right" style={{ width: "32mm" }} data-testid="preview-amc-heading">AMC Fee</th>}
                                    {anyAnnual && <th className="text-right" style={{ width: "30mm" }} data-testid="preview-annual-cost-heading">Annual Cost</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {rows}
                                <tr className="grand-total-row">
                                    <td colSpan={2} className="label">
                                        {mode === "consolidated" ? "Grand Total" : "All Options · Grand Total"}
                                    </td>
                                    <td className="figure figure-em" data-testid="preview-grand-setup">
                                        {grandSetup > 0 ? formatINR(grandSetup, currency) : ""}
                                    </td>
                                    {showAmcCol && (
                                        <td className="figure figure-em" data-testid="preview-grand-amc">
                                            {grandAmc > 0 ? formatINR(grandAmc, currency) : ""}
                                        </td>
                                    )}
                                    {anyAnnual && (
                                        <td className="figure figure-em" data-testid="preview-grand-annual-cost">
                                            {grandAnnual > 0 ? formatINR(grandAnnual, currency) : ""}
                                        </td>
                                    )}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="commercials-footnote">
                        All figures in {currency}. AMC applies from the agreed commencement date and continues annually unless otherwise agreed.
                        <span data-testid="preview-mode-tag" style={{ marginLeft: "8px", letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 600 }}>{mode}</span>
                    </div>

                    {/* Commercial Summary block — mirrors the production
                        template (.commercial-summary). Rendered whenever a
                        Special Discount applies OR GST is shown so the final
                        payable carries an unambiguous waterfall. */}
                    {(discountAmount > 0 || gstShown) && (
                        <div className="commercial-summary" data-testid="preview-summary">
                            <div className="cs-eyebrow">Commercial Summary</div>
                            <div className="cs-row">
                                <span>Standard Commercial Value</span>
                                <span>{formatINR(grandSetup, currency)}</span>
                            </div>
                            {discountAmount > 0 && (
                                <div className="cs-row cs-less">
                                    <span>
                                        Less: Special Discount
                                        {discount.type === "percent" && <span className="cs-pct">({discVal}%)</span>}
                                    </span>
                                    <span>−{formatINR(discountAmount, currency)}</span>
                                </div>
                            )}
                            <div className="cs-row cs-net">
                                <span>Net Commercial Value</span>
                                <span data-testid="preview-final-setup">{formatINR(finalSetupFee, currency)}</span>
                            </div>
                            {gstShown && (
                                <div className="cs-row cs-tax">
                                    <span>Plus: GST on Net Commercial Value <span className="cs-pct">({gstPct}%)</span></span>
                                    <span>+{formatINR(setupGst, currency)}</span>
                                </div>
                            )}
                            <div className="cs-row cs-grand">
                                <span data-testid="preview-setup-total-label">{gstShown ? "Setup Total including GST" : "Setup Total (excl. GST)"}</span>
                                <span data-testid="preview-setup-total">{formatINR(grandTotalPayable, currency)}</span>
                            </div>
                            {discount.reason && (
                                <div className="cs-footnote">{discount.reason}</div>
                            )}
                        </div>
                    )}
                    {/* Iter 97.8 — Delivery-mode legend. Rendered once at
                        the bottom of the Commercials section so the reader
                        can decode the compact icons shown alongside each
                        product. Only includes vocabulary actually used. */}
                    {usedDeliveryCodes.length > 0 && (
                        <div className="commercials-legend" data-testid="preview-delivery-legend">
                            <div className="commercials-legend-title">Delivery Modes</div>
                            <div className="commercials-legend-items">
                                {usedDeliveryCodes.map((dm) => (
                                    <span
                                        key={dm.code}
                                        className="commercials-legend-item"
                                        data-testid={`preview-delivery-legend-${dm.code}`}
                                    >
                                        <DeliveryIcon iconName={dm.iconName} label={dm.label} size={14} />
                                        <span>{dm.label}</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </section>
            </div>

            <style>{REEDOS_PRINT_STYLES}</style>
        </div>
    );
}
